// Knowledge base: markdown articles bundled from src/kb/<audience>/[<folders>/]<article>.md.
// Each article starts with front matter (title, audience, last_reviewed, related, aliases, order).
// A folder may hold an index.md (title, description, order) that names the section.

export const AUDIENCE_LABEL = {
  worker: "For support workers",
  participant: "For participants & guardians",
  office: "For office staff",
  admin: "For admins",
};
export const AUDIENCES = ["worker", "participant", "office", "admin"];
const PATH_RE = /^\.\/(worker|participant|office|admin)\/(.+)\.md$/;

// Minimal front-matter parser — handles `key: value`, trailing `# comments`,
// `key: []` and `key:` followed by `  - item` lines. Enough for our own articles.
export function parseArticle(raw) {
  const text = (raw || "").replace(/\r\n/g, "\n");
  const m = text.match(/^---\n([\s\S]*?)\n---\n?/);
  if (!m) return { meta: {}, body: text.trim() };
  const meta = {};
  let listKey = null;
  for (const line of m[1].split("\n")) {
    const item = line.match(/^\s+-\s+(.*)$/);
    if (item && listKey) { meta[listKey].push(item[1].trim()); continue; }
    const kv = line.match(/^([A-Za-z_]+):\s*(.*)$/);
    if (!kv) continue;
    const val = kv[2].replace(/\s+#.*$/, "").trim();
    if (val === "") { listKey = kv[1]; meta[listKey] = []; }
    else { listKey = null; meta[kv[1]] = val === "[]" ? [] : val.replace(/^(["'])(.*)\1$/, "$2"); }
  }
  return { meta, body: text.slice(m[0].length).trim() };
}

// Screenshots live in src/kb/_images (one flat folder, bundled by webpack as URLs).
function imageEntries() {
  try {
    const ctx = require.context("../kb", true, /\.(png|jpe?g|gif|webp)$/);
    return Object.fromEntries(ctx.keys().map((key) => [key.replace(/^\.\//, ""), ctx(key)]));
  } catch {
    return {}; // not running under webpack (e.g. Jest)
  }
}
let imageCache = null;

// Resolve an image path written in an article (relative to the article, e.g.
// ../../_images/x.png) to a path under src/kb (_images/x.png). Null for external/absolute URLs.
export function resolveImagePath(fromSlug, src) {
  if (!src || /^([a-z]+:|\/\/|\/)/i.test(src)) return null;
  const parts = fromSlug.split("/").slice(0, -1);
  for (const seg of src.split("?")[0].split("/")) {
    if (seg === "..") parts.pop();
    else if (seg !== "." && seg !== "") parts.push(seg);
  }
  return parts.join("/");
}

// The bundled URL for an article's image, or null if it isn't one of ours.
export function imageUrl(fromSlug, src) {
  const path = resolveImagePath(fromSlug, src);
  if (!path) return null;
  const url = (imageCache ||= imageEntries())[path];
  return typeof url === "string" ? url : url?.default || null;
}

const dirOf = (path) => path.split("/").slice(0, -1).join("/");
const num = (v) => (v !== undefined && v !== "" && !Number.isNaN(Number(v)) ? Number(v) : 999);
const titleCase = (s) => s.replace(/[-_]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// Article files are bundled by webpack's default handling as URLs (no build config
// needed), so paths are known synchronously but content is fetched once, lazily.
function fileEntries() {
  try {
    const ctx = require.context("../kb", true, /\.md$/);
    return ctx.keys().flatMap((key) => {
      const p = key.match(PATH_RE);
      return p ? [{ path: `${p[1]}/${p[2]}`, mod: ctx(key) }] : [];
    });
  } catch {
    return []; // not running under webpack (e.g. Jest)
  }
}

async function readSource(mod) {
  const v = typeof mod === "string" ? mod : mod?.default;
  if (typeof v !== "string") return "";
  if (v.startsWith("---") || v.includes("\n")) return v; // already the raw text
  const res = await fetch(v);
  if (!res.ok) throw new Error(`Could not load help article (${res.status})`);
  return res.text();
}

// Build the in-memory KB from [{path, raw}] — shared by the app and the content tests.
export function buildKb(files) {
  const articles = [];
  const sections = {};
  for (const { path, raw } of files) {
    const { meta, body } = parseArticle(raw);
    if (path.endsWith("/index")) {
      sections[dirOf(path)] = { title: meta.title, description: meta.description, order: num(meta.order) };
      continue;
    }
    articles.push({
      slug: path,
      audience: path.split("/")[0],
      folder: dirOf(path),
      title: meta.title || titleCase(path.split("/").pop()),
      lastReviewed: meta.last_reviewed || null,
      related: meta.related || [],
      aliases: meta.aliases || [],
      pages: meta.pages || [],
      order: num(meta.order),
      body,
    });
  }
  // An article's own folder (same path as the article, minus .md) holds its sub-articles.
  const bySlug = Object.fromEntries(articles.map((a) => [a.slug, a]));
  for (const a of articles) { a.parent = bySlug[a.folder] ? a.folder : null; a.children = []; }
  for (const a of articles) if (a.parent) bySlug[a.parent].children.push(a);
  articles.forEach((a) => a.children.sort(byOrder));
  return { articles, sections };
}

let loading = null;
// Resolves to { articles, sections } (unfiltered); callers filter by audience.
export function loadKb() {
  return (loading ||= Promise.all(fileEntries().map(async ({ path, mod }) => ({ path, raw: await readSource(mod) })))
    .then(buildKb)
    .catch((e) => { loading = null; throw e; }));
}

// Which article audiences a signed-in user may read.
export function audiencesFor({ isWorker, isPortal, role } = {}) {
  if (isWorker) return ["worker"];
  if (isPortal) return ["participant"];
  if (role === "Admin") return [...AUDIENCES];
  return ["office", "worker", "participant"];
}

// Synchronous (from file paths only) so shells can decide whether to show a Help button.
export const hasArticles = (audiences) =>
  fileEntries().some(({ path }) => !path.endsWith("/index") && audiences.includes(path.split("/")[0]));

// Find by current slug or by an old slug listed in `aliases`; null if hidden from this reader.
export function findArticle(kb, slug, audiences) {
  const a = kb.articles.find((x) => x.slug === slug) || kb.articles.find((x) => x.aliases.includes(slug));
  return a && audiences.includes(a.audience) ? a : null;
}

const byOrder = (a, b) => a.order - b.order || a.title.localeCompare(b.title);

// Nested sections per audience: [{ audience, label, root: { children[], articles[] } }]
export function buildTree(kb, audiences) {
  return AUDIENCES.filter((a) => audiences.includes(a)).map((audience) => {
    const root = { path: audience, title: AUDIENCE_LABEL[audience], children: [], articles: [], order: 0 };
    const nodes = { [audience]: root };
    const ensure = (path) => {
      if (nodes[path]) return nodes[path];
      const meta = kb.sections[path] || {};
      const node = {
        path, title: meta.title || titleCase(path.split("/").pop()), description: meta.description,
        order: meta.order ?? 999, children: [], articles: [],
      };
      ensure(dirOf(path)).children.push(node);
      return (nodes[path] = node);
    };
    // Sub-articles hang off their parent article, not off a folder section.
    for (const a of kb.articles.filter((x) => x.audience === audience && !x.parent)) ensure(a.folder).articles.push(a);
    const sort = (n) => { n.children.sort(byOrder); n.articles.sort(byOrder); n.children.forEach(sort); };
    sort(root);
    return { audience, label: AUDIENCE_LABEL[audience], root };
  }).filter((g) => g.root.children.length > 0 || g.root.articles.length > 0);
}

const withSubs = (a) => 1 + a.children.reduce((n, c) => n + withSubs(c), 0);
export const countArticles = (node) => node.articles.reduce((n, a) => n + withSubs(a), 0) + node.children.reduce((n, c) => n + countArticles(c), 0);

// "Shifts › Clocking in & out" style trail of section titles for an article.
export function breadcrumb(kb, article) {
  const parts = article.folder.split("/").slice(1);
  return parts.map((_, i) => {
    const path = [article.audience, ...parts.slice(0, i + 1)].join("/");
    // A folder that is itself an article (a parent with sub-articles) shows that article's title.
    return kb.articles.find((a) => a.slug === path)?.title || kb.sections[path]?.title || titleCase(parts[i]);
  });
}

// All words must appear in the title or body; title matches rank first.
export function searchArticles(kb, query, audiences) {
  const terms = (query || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  return kb.articles
    .filter((a) => audiences.includes(a.audience))
    .map((a) => {
      const title = a.title.toLowerCase();
      const hay = `${title}\n${a.body.toLowerCase()}`;
      return terms.every((t) => hay.includes(t)) ? { a, score: terms.filter((t) => title.includes(t)).length } : null;
    })
    .filter(Boolean)
    .sort((x, y) => y.score - x.score || x.a.title.localeCompare(y.a.title))
    .map((x) => x.a);
}

// Does an app route match an article's `pages:` pattern? A trailing `/*` matches that
// path and anything below it (`/worker/shift/*` covers `/worker/shift/SHIFT-1`); otherwise exact.
export function pageMatches(pattern, pathname) {
  const clean = (p) => (p.length > 1 ? p.replace(/\/+$/, "") : p);
  const path = clean(pathname);
  if (pattern.endsWith("/*")) {
    const base = clean(pattern.slice(0, -2));
    return path === base || path.startsWith(base + "/");
  }
  return clean(pattern) === path;
}

// Articles this reader can see that are about the page they are on.
export function articlesForPage(kb, pathname, audiences) {
  return kb.articles
    .filter((a) => audiences.includes(a.audience) && a.pages.some((p) => pageMatches(p, pathname)))
    .sort(byOrder);
}

// Resolve a relative article link (../office/foo.md) found in `fromSlug`'s body.
export function resolveArticleLink(fromSlug, href) {
  if (!href || /^[a-z]+:/i.test(href) || !/\.md(#.*)?$/.test(href)) return null;
  const parts = fromSlug.split("/").slice(0, -1);
  for (const seg of href.replace(/#.*$/, "").split("/")) {
    if (seg === "..") parts.pop();
    else if (seg !== "." && seg !== "") parts.push(seg);
  }
  return parts.join("/").replace(/\.md$/, "");
}
